import { AvailabilityFieldDto } from '@/features/dashboard/tours/types/AvailabilityFieldDto.ts';
import { pluralForm } from '@/utils/pluralize.ts';

/**
 * Obłożenie w liczbach — jedno miejsce dla całego ekranu wyjazdu.
 *
 * Procenty i „wolne miejsca” nie jadą w DTO: to wynik dzielenia dwóch pól,
 * które i tak przyjeżdżają. Gdyby backend przysyłał gotowy procent, byłyby
 * dwa źródła prawdy i pytanie, które zaokrąglenie wygrywa.
 *
 * `limit: null` znaczy „bez limitu” — wtedy nie ma ani procentu, ani wolnych
 * miejsc, bo nie ma sufitu, względem którego dałoby się je policzyć.
 */

/** Udział zajętych miejsc, 0–100. `null` przy braku limitu. */
export const occupancyPercent = (
  availability: AvailabilityFieldDto,
): number | null => {
  const { currentCount, limit } = availability;
  if (limit === null || limit <= 0) return null;
  // Nadkomplet (limit obniżony po zapisach) nie ma rozpychać paska.
  return Math.min(100, Math.round((currentCount / limit) * 100));
};

/** Ile miejsc zostało. `null` przy braku limitu, nigdy liczba ujemna. */
export const freePlaces = (
  availability: AvailabilityFieldDto,
): number | null => {
  const { currentCount, limit } = availability;
  if (limit === null) return null;
  return Math.max(0, limit - currentCount);
};

export const isFull = (availability: AvailabilityFieldDto): boolean => {
  const { currentCount, limit } = availability;
  return limit !== null && currentCount >= limit;
};

/** „3 wolne miejsca”, „bez limitu miejsc”, „brak wolnych miejsc”. */
export const freePlacesLabel = (availability: AvailabilityFieldDto): string => {
  const free = freePlaces(availability);
  if (free === null) return 'bez limitu miejsc';
  if (free === 0) return 'brak wolnych miejsc';
  return `${free} ${pluralForm(free, 'wolne', 'wolne', 'wolnych')} ${pluralForm(free, 'miejsce', 'miejsca', 'miejsc')}`;
};

/** „7 z 22” albo samo „7”, gdy limitu nie ma. */
export const occupancyLabel = (availability: AvailabilityFieldDto): string => {
  const { currentCount, limit } = availability;
  return limit === null ? String(currentCount) : `${currentCount} z ${limit}`;
};
